import {
  Box,
  Typography,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
  Stack,
  alpha,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import Loader from "Components/Loader";
import ImgPath from "Constants/Imgs";
import { CaseListType } from "DataTypes/Case.type";
import { UserInitialStateType } from "DataTypes/Redux";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CaseApi from "Service/Case.api";
import {
  disconnectChatHub,
  connection,
  offGlobalMessagesRead,
  ensureConnected
} from "Service/signalRService";
import { useNotifications } from "Components/Notification/NotificationContext";
import DateTime from "Utils/DateTime";
import { FormateCaseList } from "./Formatter";
import {
  filterByAnyValue,
  FormatDropdownData,
  HandleFormChange,
  formatUserType,
} from "Utils/Common";
import { useParams, useLocation } from "react-router-dom";
import { GlobalResType, HandleChangeProps } from "DataTypes/Common";
import { DropdownArrPropsType, FormDataType } from "DataTypes/Form";
import ModalLayout from "Layouts/Modal";
import { CustomModal } from "Components/Modal";
import { AddNewCaseForm } from "./Data";
import StatusApi from "Service/Status.api";

const ChatListComponent = (props: any) => {
  const { setShowChatList, getSelectedCase, onListLoaded, selectedCase } = props;
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const location = useLocation();
  const paymentStatusFilter = location.state?.paymentStatus;

  const { orderType } = useParams<{ orderType: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [caseList, setCaseList] = useState<CaseListType[]>([]);
  const [orderDropdown, setOrderDropdown] = useState<DropdownArrPropsType[]>(
    []
  );
  const [caseDropdown, setCaseDropdown] = useState<DropdownArrPropsType[]>([]);
  const [caseListCopy, setCaseListCopy] = useState<CaseListType[]>([]);
  const [createCaseFormData, setCreateCaseFormData] = useState<FormDataType[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [inputVal, setInputVal] = useState<string>("");
  const [isLegalCounselChat, setIsLegalCounselChat] = useState<boolean>(false);
  const [finalStatuses, setFinalStatuses] = useState<string[]>([]);
  const [parentViewCaseId, setParentViewCaseId] = useState<number | null>(null);

  // ── Real-time unread badge tracking ─────────────────────────────────────
  // STRATEGY: We hook into the ReceiveNotification event (user-targeted, not
  // group-targeted) which fires for every incoming message regardless of
  // whether the user has joined the SignalR case group. This is the same
  // mechanism the notification bell uses, so it works in real-time.

  const selectedCaseRef = useRef<any>(null);
  useEffect(() => { selectedCaseRef.current = selectedCase; }, [selectedCase]);

  // Access the live notifications stream from NotificationContext
  const { notifications } = useNotifications();
  const processedNotifIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Scan new notifications to find incoming chat messages for cases we know
    notifications.forEach((notif) => {
      if (notif.isRead) return;                         // Already read — skip
      if (processedNotifIds.current.has(notif.id)) return; // Already processed

      // Parse caseId from notification metadata or link
      let notifCaseId = 0;
      try {
        const meta = notif.metadata
          ? (typeof notif.metadata === "string" ? JSON.parse(notif.metadata) : notif.metadata)
          : {};
        notifCaseId = Number(meta.CaseId || meta.caseId || 0);
      } catch (_) { }

      if (!notifCaseId) {
        // Fallback: parse from link like "/chat/401/messages"
        const linkMatch = (notif.link || "").match(/\/chat\/(\d+)/i);
        if (linkMatch) notifCaseId = Number(linkMatch[1]);
      }

      if (!notifCaseId) return;

      // Don't badge the currently-open chat
      const currentCaseId = Number(selectedCaseRef.current?.caseId ?? 0);
      if (notifCaseId === currentCaseId) {
        processedNotifIds.current.add(notif.id);
        return;
      }

      // Mark as processed so we don't double-count
      processedNotifIds.current.add(notif.id);

      // Increment unread for that case in the sidebar
      setCaseList(prev => prev.map(c =>
        c.caseId === notifCaseId
          ? { ...c, unreadCount: (c.unreadCount ?? 0) + 1 }
          : c
      ));
      setCaseListCopy(prev => prev.map(c =>
        c.caseId === notifCaseId
          ? { ...c, unreadCount: (c.unreadCount ?? 0) + 1 }
          : c
      ));
    });
  }, [notifications]);

  // Handler: MessagesRead SignalR event → zero-out badge when we open a chat
  const handleGlobalRead = useCallback((data: any) => {
    const readCaseId = Number(data?.CaseId ?? data?.caseId ?? 0);
    if (!readCaseId) return;
    const readByUserId = Number(data?.ReadByUserId ?? 0);
    // Only clear OUR badge (not someone else's read event)
    if (readByUserId !== myInfo?.id) return;

    setCaseList(prev => prev.map(c =>
      c.caseId === readCaseId ? { ...c, unreadCount: 0 } : c
    ));
    setCaseListCopy(prev => prev.map(c =>
      c.caseId === readCaseId ? { ...c, unreadCount: 0 } : c
    ));
  }, [myInfo?.id]);

  // Register MessagesRead listener via the live SignalR connection
  useEffect(() => {
    let registered = false;

    const tryRegister = async () => {
      try {
        await ensureConnected();
        if (connection) {
          connection.on("MessagesRead", handleGlobalRead);
          registered = true;
        }
      } catch (err) {
        console.warn("[ChatList] Could not register MessagesRead listener:", err);
      }
    };

    tryRegister();

    return () => {
      if (registered && connection) {
        offGlobalMessagesRead(handleGlobalRead);
      }
    };
  }, [handleGlobalRead]);
  // Synchronize unreadCount: clear badge for the currently selected/open chat
  useEffect(() => {
    if (selectedCase?.caseId) {
      const activeId = Number(selectedCase.caseId);
      setCaseList(prev => prev.map(c =>
        c.caseId === activeId ? { ...c, unreadCount: 0 } : c
      ));
      setCaseListCopy(prev => prev.map(c =>
        c.caseId === activeId ? { ...c, unreadCount: 0 } : c
      ));
    }
  }, [selectedCase?.caseId]);

  const getChatListRef = useRef(getChatListByUserId);
  useEffect(() => {
    getChatListRef.current = getChatListByUserId;
  });

  useEffect(() => {
    const handleUpdate = () => {
      getChatListRef.current();
    };

    window.addEventListener("notificationsUpdated", handleUpdate);
    window.addEventListener("ReceiveReadReceipt", handleUpdate);

    return () => {
      window.removeEventListener("notificationsUpdated", handleUpdate);
      window.removeEventListener("ReceiveReadReceipt", handleUpdate);
    };
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    handleOnload();
  }, [orderType]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      getChatListByUserId();
    }, 500); // Debounce search for 500ms

    return () => clearTimeout(delayDebounceFn);
  }, [inputVal]);

  useEffect(() => {
    return () => {
      // cleanup on chat screen exit
      // disconnectChatHub();
    };
  }, []);

  async function handleOnload() {
    setLoading(true);
    setIsLegalCounselChat(false);
    const caseRes: GlobalResType = await CaseApi.fetchCaseTypes();
    let caseTypeDropList: DropdownArrPropsType[] = [];
    let orderTypeDropList: DropdownArrPropsType[] = [];
    if (caseRes?.success) {
      caseTypeDropList = FormatDropdownData(caseRes?.data, "id", "name", "id");
      setCaseDropdown(caseTypeDropList);
    }

    const OrderTypeRes: GlobalResType = await CaseApi.fetchOrderTypes();

    if (OrderTypeRes?.success) {
      orderTypeDropList = FormatDropdownData(
        OrderTypeRes?.data,
        "id",
        "name",
        "id"
      );
      setOrderDropdown(orderTypeDropList);
    }

    let localFinalStatuses: string[] = [];
    try {
      const statusRes = await StatusApi.getFinalStatuses();
      if (statusRes?.data) {
        // Handle both possible shapes depending on Api.tsx wrapping
        localFinalStatuses = Array.isArray(statusRes.data) ? statusRes.data : (statusRes.data.data || []);
        setFinalStatuses(localFinalStatuses);
      }
    } catch (err) {
      console.error("Error fetching final statuses", err);
    }

    //     // 1. Define your allowed legal IDs
    // const targetIds = [43, 44, 51];
    // // Check if the current order is in our list
    // const isTargetOrder = targetIds.includes(Number(orderType));

    // // Assign the object only if it's a target order
    // const legalObject = isTargetOrder
    //   ? orderTypeDropList?.find(el => Number(el.id) === Number(orderType))
    //   : null;
    // // console.log(legalObject, "legalObject");
    // if (legalObject) {
    //   if (legalObject.value == orderType) {
    //     setIsLegalCounselChat(true);
    //   }
    // }
    await getChatListByUserId(localFinalStatuses);
    setLoading(false);
  }

  useEffect(() => {
    // Dynamically check if the button should be active based on URL OR selected chat case
    const targetIds = [43, 44, 51, 72];
    const currentOrderTypeId = orderType
      ? Number(orderType)
      : selectedCase?.orderTypeId ? Number(selectedCase.orderTypeId) : null;

    if (currentOrderTypeId && targetIds.includes(currentOrderTypeId)) {
      setIsLegalCounselChat(true);
    } else {
      setIsLegalCounselChat(false);
    }
  }, [orderType, selectedCase]);

  // Ensure parentViewCaseId stays in sync if selectedCase is set externally (e.g., via location state)
  useEffect(() => {
    if (selectedCase && isLegalCounselChat && (!selectedCase.parentCaseId || selectedCase.parentCaseId === 0)) {
      setParentViewCaseId(selectedCase.caseId);
    } else if (!isLegalCounselChat) {
      setParentViewCaseId(null);
    }
  }, [selectedCase, isLegalCounselChat]);

  async function getChatListByUserId(passedFinalStatuses?: string[]) {
    // If orderType is "admin-support", pass 53 (Admin Support ID), else pass orderType
    const isAdminSupportRoute = orderType === "admin-support";
    const orderTypeIdToPass = isAdminSupportRoute ? 53 : (orderType ?? null);

    setLoading(true);

    // OLD CODE:
    // const res = await CaseApi.getCaseListByUserId({
    //   clientId: myInfo.id,
    //   orderTypeId: orderTypeIdToPass,
    //   searchQuery: inputVal, // Pass the search query to the backend
    //   pageNumber: 1,
    //   pageSize: 100,
    // });

    let res: any;
    if ((paymentStatusFilter === "pending" || paymentStatusFilter === "failed") && (myInfo?.userType === "ADMIN" || myInfo?.userType === "SUPERADMIN")) {
      res = await CaseApi.getAdminPendingFailedOrders({
        pageNumber: 1,
        pageSize: 100,
        searchQuery: inputVal,
        orderTypeId: orderTypeIdToPass,
      });
    } else if (paymentStatusFilter === "completed" && (myInfo?.userType === "ADMIN" || myInfo?.userType === "SUPERADMIN")) {
      res = await CaseApi.getAdminCompletedOrders({
        pageNumber: 1,
        pageSize: 100,
        searchQuery: inputVal,
        orderTypeId: orderTypeIdToPass,
      });
    } else {
      res = await CaseApi.getCaseListByUserId({
        clientId: myInfo.id,
        orderTypeId: orderTypeIdToPass,
        searchQuery: inputVal,
        pageNumber: 1,
        pageSize: 100,
      });
    }

    if (res?.success) {
      // res.data is now a PaginatedListDto, so we need res.data.data
      let formattedRes = FormateCaseList(res?.data?.data);
      console.log(formattedRes, "formattedRes")

      // Filter out rejected and pending respondent ODR cases from active chat list for clients/respondents
      if (myInfo?.userType !== "ADMIN" && myInfo?.userType !== "SUPERADMIN") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const orderTypeName = (c.orderType || "").toLowerCase();
          const isOdr = ["online mediation", "online arbitration", "arbitration", "online negotiation", "mediation", "negotiation"].some(type => orderTypeName.includes(type));
          if (isOdr) {
            // 1. Filter out rejected ODR cases from chat list
            const isRejected = c.respondentConsentStatus === "Rejected" || c.respondentConsentStatus === "Declined" || c.odrStatus === "REJECTED";
            if (isRejected) {
              return false;
            }

            // 2. ONLY for Respondent users: Filter out ODR cases where respondent has not accepted consent yet
            const isClaimant = Number(c.clientId) === Number(myInfo?.id);
            const isRespondentUser = !isClaimant && myInfo?.userType !== "PROFESSIONAL";

            if (isRespondentUser) {
              const respondentStatus = (c.respondentConsentStatus || "").trim().toUpperCase();
              const isRespondentAccepted = respondentStatus === "CONFIRMED" || respondentStatus === "ACCEPTED" || respondentStatus === "GIVEN";
              if (!isRespondentAccepted) {
                return false;
              }
            }
          }
          return true;
        });
      }

      // Filter out pending ODR cases for professionals until they accept the neutral invitation
      if (myInfo?.userType === "PROFESSIONAL") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const orderTypeName = (c.orderType || "").toLowerCase();
          const isOdr = ["online mediation", "online arbitration", "arbitration", "online negotiation", "mediation", "negotiation"].some(type => orderTypeName.includes(type));
          if (isOdr) {
            const neutralStatus = (c.neutralConsentStatus || "").trim().toUpperCase();
            const isAccepted = neutralStatus === "CONFIRMED" || neutralStatus === "ACCEPTED" || neutralStatus === "GIVEN";
            if (!isAccepted) {
              return false;
            }
          }
          return true;
        });
      }

      if (isAdminSupportRoute) {
        // Only show "Admin support" cases
        formattedRes = formattedRes.filter((c: CaseListType) =>
          c.orderType?.trim().toLowerCase() === "admin support"
        );
      } else {
        if (!orderType) {
          formattedRes = formattedRes.filter((c: CaseListType) =>
            c.orderType?.trim().toLowerCase() !== "admin support"
          );
        }
      }

      const isCaseCompleted = (c: CaseListType) => {
        const statusName = c.currentProcessingStatusName;
        if (!statusName) return false;
        return statusName.trim().toLowerCase() === "completed";
      };

      if (paymentStatusFilter === "completed") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          return isCaseCompleted(c);
        });
      } else if (paymentStatusFilter === "active") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const isCompleted = isCaseCompleted(c);
          const status = c.casePaymentStatus?.toLowerCase() || "pending";
          const isPaid = status === "paymentcompleted" || status === "success" || status === "completed";
          const isFree = c.casePrice === 0; // null is not free
          return !isCompleted && (isPaid || isFree);
        });
      } else if (paymentStatusFilter === "pending") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const isCompleted = isCaseCompleted(c);
          const status = c.casePaymentStatus?.toLowerCase() || "pending";
          const isFree = c.casePrice === 0;
          return !isCompleted && !isFree && (status === "pending" || status === "paymentpending");
        });
      } else if (paymentStatusFilter === "failed") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const isCompleted = isCaseCompleted(c);
          const status = c.casePaymentStatus?.toLowerCase() || "pending";
          const isFree = c.casePrice === 0;
          return !isCompleted && !isFree && (status === "failed" || status === "paymentfailed" || status === "rejected");
        });
      }

      formattedRes.sort((a: any, b: any) => {
        const timeA = new Date(a.latestMessageTimestamp || a.statusLastUpdated || a.dateCreated).getTime();
        const timeB = new Date(b.latestMessageTimestamp || b.statusLastUpdated || b.dateCreated).getTime();
        return timeB - timeA;
      });

      setCaseList(formattedRes);
      setCaseListCopy(formattedRes);
      // This sends the data back to the Parent
      if (onListLoaded) onListLoaded(formattedRes);
    }
    setLoading(false);
  }

  const getInitials: (orderType: string) => string = (orderType) => {
    if (!orderType) return "";
    const words = orderType.split(" ");
    let initials = "";
    for (let i = 0; i < words.length && i < 2; i++) {
      initials += words[i].charAt(0).toUpperCase();
    }
    return initials;
  };

  const handleCreateCase = async () => {

    // 1. Define your allowed legal IDs
    const targetIds = [43, 44, 51, 72];

    // 2. Dynamically get current Order Type
    const currentOrderTypeId = orderType
      ? Number(orderType)
      : selectedCase?.orderTypeId ? Number(selectedCase.orderTypeId) : null;

    // 3. Find the specific object
    const findCase = orderDropdown?.find((el) =>
      Number(el.id) === currentOrderTypeId && targetIds.includes(Number(el.id))
    );

    const payload = {
      clientId: myInfo.id,
      caseTypeId: findCase?.value ?? null,
      orderTypeId: currentOrderTypeId,
      description: createCaseFormData[0].value,
      parentCaseId: parentViewCaseId || null,
    };
    const res = await CaseApi.createCase(payload);
    if (res?.success) {
      setModalOpen(false);
      setCreateCaseFormData(JSON.parse(JSON.stringify(AddNewCaseForm)));
      getChatListByUserId();
    }
  };

  const HandleFormDataChange = (
    data: HandleChangeProps,
    arr: FormDataType[]
  ) => {
    setCreateCaseFormData(HandleFormChange(data, arr));
  };

  return (
    <>
      <ModalLayout
        content={
          <CustomModal.CreateCaseModal
            formArr={createCaseFormData}
            handleChange={HandleFormDataChange}
          />
        }
        open={modalOpen}
        modwidth="400px"
        onClose={() => setModalOpen(false)}
        heading="New Case"
        buttonArr={[
          {
            id: "createCase",
            label: "Create Case",
            clickHandler: handleCreateCase,
          },
          {
            id: "cancel",
            label: "Cancel",
            clickHandler: () => setModalOpen(false),
            type: "border",
          },
        ]}
      />
      <Box
        sx={{
          width: { xs: "100%", sm: 350 },
          height: "100%",
          // Version 4: Lightweight White Glass
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          borderRight: "1px solid rgba(0, 0, 0, 0.08)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {/* Header and Search */}
        <Box
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2.5}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: "#fff",
                letterSpacing: "-0.5px",
              }}
            >
              Messages
            </Typography>
            {isLegalCounselChat && ["USER", "CORPORATEUSER", "NRIUSER"].includes(myInfo?.userType) && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCreateCaseFormData(
                    JSON.parse(JSON.stringify(AddNewCaseForm))
                  );
                  setModalOpen(true);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(255, 255, 255, 0.1)",
                  fontWeight: 600,
                  bgcolor: "#fff",
                  color: "#1a237e",
                  "&:hover": {
                    bgcolor: alpha("#fff", 0.9)
                  }
                }}
              >
                New Case
              </Button>
            )}
          </Stack>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              px: 2,
              py: 1.25,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              "&:focus-within": {
                backgroundColor: "rgba(255, 255, 255, 1)",
                boxShadow: "0 4px 25px rgba(0, 0, 0, 0.2)",
                borderColor: "#fff",
                "& .MuiSvgIcon-root": { color: "#1a237e" },
                "& input": { color: "#1a237e" }
              },
            }}
          >
            <SearchIcon sx={{ color: "rgba(255, 255, 255, 0.5)", mr: 1, fontSize: 20 }} />
            <InputBase
              placeholder="Search conversations..."
              fullWidth
              sx={{ fontSize: "14px", fontWeight: 500, color: "#fff" }}
              inputProps={{ "aria-label": "search" }}
              onChange={(e) => setInputVal(e.target.value)}
            />
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Loader />
          </Box>
        ) : (
          <List
            disablePadding
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0, // Important for flex child to be scrollable
            }}
          >
            {parentViewCaseId && ["USER", "CORPORATEUSER", "NRIUSER"].includes(myInfo?.userType) && (
              <ListItem
                sx={{
                  mx: 1, mb: 1, width: "calc(100% - 16px)", borderRadius: "16px", cursor: "pointer",
                  backgroundColor: alpha("#1a237e", 0.08),
                  "&:hover": { backgroundColor: alpha("#1a237e", 0.12) },
                  py: 1, px: 2, display: "flex", justifyContent: "center",
                }}
                onClick={() => setParentViewCaseId(null)}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a237e" }}>
                  ← Back to Main List
                </Typography>
              </ListItem>
            )}

            {(["USER", "CORPORATEUSER", "NRIUSER"].includes(myInfo?.userType)
              ? (parentViewCaseId
                ? caseList.filter(c => {
                  const isMatch = Number(c.caseId) === Number(parentViewCaseId) || Number(c.parentCaseId) === Number(parentViewCaseId);
                  return isMatch;
                })
                : caseList.filter(c => !c.parentCaseId))
              : caseList
            ).map((chat: CaseListType, index) => (
              <React.Fragment key={chat.caseId}>
                <ListItem
                  sx={{
                    mx: 1,
                    mb: 1,
                    width: "calc(100% - 16px)",
                    borderRadius: "16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: selectedCase?.caseId == chat.caseId
                      ? alpha("#1a237e", 0.06)
                      : "transparent",
                    "&:hover": {
                      backgroundColor: alpha("#1a237e", 0.04),
                      transform: "translateY(-1px)",
                    },
                    alignItems: "center",
                    py: 2,
                    px: 2,
                    ...(selectedCase?.caseId == chat.caseId && {
                      boxShadow: "0 4px 12px rgba(26, 35, 126, 0.1)",
                      borderLeft: "4px solid #1a237e"
                    })
                  }}
                  onClick={() => {
                    if (isMobile) setShowChatList(false);
                    getSelectedCase(chat);

                    // Optimistically clear the unread badge immediately when user opens the chat
                    if ((chat.unreadCount ?? 0) > 0) {
                      setCaseList(prev => prev.map(c =>
                        c.caseId === chat.caseId ? { ...c, unreadCount: 0 } : c
                      ));
                      setCaseListCopy(prev => prev.map(c =>
                        c.caseId === chat.caseId ? { ...c, unreadCount: 0 } : c
                      ));
                    }

                    // If it's a client user, and a parent case of specific order types, open the folder view!
                    if (["USER", "CORPORATEUSER", "NRIUSER"].includes(myInfo?.userType)) {
                      const folderTargetIds = [43, 44, 51, 72];
                      if (!chat.parentCaseId && folderTargetIds.includes(Number(chat.orderTypeId))) {
                        setParentViewCaseId(chat.caseId);
                      }
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      alt={chat.caseType}
                      sx={{
                        width: 48,
                        height: 48,
                        background: selectedCase?.caseId == chat.caseId
                          ? "linear-gradient(135deg, #1a237e, #3949ab)"
                          : "#f0f2f5",
                        color: selectedCase?.caseId == chat.caseId ? "#fff" : "#1a237e",
                        fontSize: "16px",
                        fontWeight: "bold",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      }}
                    >
                      {getInitials(chat.orderType)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="baseline"
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "15px",
                            fontWeight: 700,
                            color: "#1a237e",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "180px",
                          }}
                        >
                          {chat.parentCaseId
                            ? `${chat?.description} - ${chat.caseId}`
                            : (myInfo.userType === "ADMIN" || myInfo.userType === "SUPERADMIN")
                              ? (chat.orderType === "Admin Support" || chat.orderTypeId === 53)
                                ? (chat.clientUserType === "CORPORATEUSER"
                                  ? `${chat.clientFullName} (${chat.clientOrganizationName || "N/A"}) `
                                  : `${chat.clientFullName} - (${formatUserType(chat.clientUserType)}) `)
                                : `${chat.clientFullName} - ${chat.orderType} - ${chat.caseNumber ? chat.caseNumber : chat.caseId}`
                              : `${chat.orderType} - ${chat.caseNumber ? chat.caseNumber : chat.caseId} - ${chat.caseType}`}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                          <Typography variant="caption" sx={{ color: "text.primary", opacity: 0.7 }}>
                            {DateTime.ToLocalDate(chat.latestMessageTimestamp || chat.statusLastUpdated)}
                          </Typography>
                          {Number(chat.unreadCount) > 0 && (
                            <Chip
                              label={chat.unreadCount}
                              size="small"
                              sx={{
                                height: 22,
                                borderRadius: "12px",
                                bgcolor: "#25D366",
                                color: "#ffffff",
                                fontSize: "0.75rem",
                                fontWeight: "bold",
                                minWidth: 22,
                                px: 0.5,
                                boxShadow: "0 2px 6px rgba(37, 211, 102, 0.4)"
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.primary",
                            display: "block",
                            fontSize: "12px",
                            opacity: 0.6,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "200px"
                          }}
                        >
                          {chat.latestMessageContent ? chat.latestMessageContent : (chat.caseNumber ? `Case ID: ${chat.caseNumber}` : `Case ID: ${chat.caseId}`)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </>
  );
};
export default ChatListComponent;

const sxSelected = {
  backgroundColor: "#f3ecebff",
};
